import { MoreHorizontal, MapPin } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { useState, useRef } from 'react'
import PostActions from './PostActions'
import PostHeader from './PostHeader'
import VideoPlayer from './VideoPlayer'
import Avatar from './ui/Avatar'
import PostOptionsDropdown from './PostOptionsDropdown'
import MapModal from './MapModal'
import LocationPermissionModal from './LocationPermissionModal'
import QuoteRepostModal from './QuoteRepostModal'
import { useMediaPreview } from '../contexts/MediaPreviewContext'
import PostAnalyticsModal from './PostAnalyticsModal'
import { useSocialInteractions } from '../hooks/useSocialInteractions'
import { useLocationPermission } from '../hooks/useLocationPermission'

interface PostProps {
  post: {
    id: string
    authorId?: string
    author: {
      name: string
      username: string
      avatar?: string
      verified?: boolean
    }
    content: string
    mediaUrl?: string
    mediaType?: string
    createdAt: string
    capturedAt?: string
    location?: {
      address?: string
    }
    stats?: {
      likes: number
      comments: number
      reposts: number
    }
    onRecordCode?: string
  }
  currentUserId?: string // To determine if user owns the post
  isGuest?: boolean // Block interactions for guest users
}

export default function Post({ post, currentUserId, isGuest = false }: PostProps) {
  const timeAgo = formatDistanceToNow(new Date(post.createdAt), { addSuffix: true })
  const navigate = useNavigate()
  const { openPreview } = useMediaPreview()
  const [showOptionsMenu, setShowOptionsMenu] = useState(false)
  const [showMapModal, setShowMapModal] = useState(false)
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [showQuoteModal, setShowQuoteModal] = useState(false)
  const [showProfileCard, setShowProfileCard] = useState(false)
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false)
  const profileTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | undefined>(undefined)
  const moreButtonRef = useRef<HTMLButtonElement>(null)

  // Location permission hook
  const { hasPermission, requestPermission } = useLocationPermission()

  // Use social interactions hook for like, save, repost
  const socialInteractions = useSocialInteractions(post.id)
  const {
    liked: isLiked,
    saved: isBookmarked,
    reposted: isReposted,
    likesCount,
    commentsCount,
    repostsCount,
    loading,
    actions: { toggleLike, toggleSave, toggleRepost, sharePost }
  } = socialInteractions

  const handleMediaClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    openPreview({
      mediaUrl: post.mediaUrl,
      mediaType: post.mediaType,
      post: {
        id: post.id,
        author: post.author,
        content: post.content,
        stats: post.stats,
        onRecordCode: post.onRecordCode,
        createdAt: post.createdAt,
        capturedAt: post.capturedAt,
        location: post.location,
      },
    })
  }

  const handlePostClick = () => {
    navigate(`/post/${post.id}`)
  }

  const handleGuestInteraction = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate('/login', { state: { from: `/post/${post.id}` } })
  }

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isGuest) {
      handleGuestInteraction(e)
      return
    }

    // Check location permission before allowing like
    if (!hasPermission) {
      setShowLocationModal(true)
      return
    }

    toggleLike()
  }

  const handleEnableLocation = async () => {
    const granted = await requestPermission()
    if (granted) {
      setShowLocationModal(false)
      // Now allow the like action
      toggleLike()
    }
  }

  const handleComment = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isGuest) {
      handleGuestInteraction(e)
      return
    }
    navigate(`/post/${post.id}`)
  }

  const handleRepost = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    if (isGuest) {
      if (e) handleGuestInteraction(e)
      return
    }
    toggleRepost()
  }

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isGuest) {
      handleGuestInteraction(e)
      return
    }
    sharePost()
  }

  const handleBookmark = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isGuest) {
      handleGuestInteraction(e)
      return
    }
    toggleSave()
  }

  const handleMoreClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (moreButtonRef.current) {
      const rect = moreButtonRef.current.getBoundingClientRect()
      setMenuPosition({ x: rect.left, y: rect.bottom })
    }

    setShowOptionsMenu(true)
  }

  // Check if current user owns the post
  const isOwner = currentUserId === post.authorId

  const handleProfileEnter = () => {
    if (profileTimeout.current) clearTimeout(profileTimeout.current)
    profileTimeout.current = setTimeout(() => setShowProfileCard(true), 400)
  }
  const handleProfileLeave = () => {
    if (profileTimeout.current) clearTimeout(profileTimeout.current)
    profileTimeout.current = setTimeout(() => setShowProfileCard(false), 300)
  }

  return (
    <article
      onClick={handlePostClick}
      className="post-item px-4 pt-3 pb-0 cursor-pointer transition-colors hover:bg-gray-50/50 border-b border-gray-200"
    >
      <div className="flex gap-3">
        {/* Avatar — left side like Twitter */}
        <div className="flex-shrink-0 pt-0.5 relative">
          <div
            onMouseEnter={handleProfileEnter}
            onMouseLeave={handleProfileLeave}
          >
            <Avatar
              src={post.author?.avatar}
              alt={post.author?.name || 'User'}
              size="lg"
              onClick={() => {
                if (post.authorId) navigate(`/profile/${post.authorId}`)
              }}
              className="cursor-pointer"
            />
          </div>

          {/* Profile Hover Card — Twitter style */}
          {showProfileCard && (
            <div
              className="absolute top-full left-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-200 p-4 z-50"
              onClick={(e) => e.stopPropagation()}
              onMouseEnter={handleProfileEnter}
              onMouseLeave={handleProfileLeave}
            >
              <div className="flex items-start justify-between mb-3">
                <Avatar
                  src={post.author?.avatar}
                  alt={post.author?.name || 'User'}
                  size="xl"
                  onClick={() => { if (post.authorId) navigate(`/profile/${post.authorId}`) }}
                  className="cursor-pointer"
                />
                {!isOwner && (
                  <button
                    onClick={(e) => { e.stopPropagation() }}
                    className="px-5 py-2 bg-gray-900 text-white rounded-full text-[14px] font-bold hover:bg-gray-800 transition-colors"
                  >
                    Follow
                  </button>
                )}
              </div>
              <div
                className="mb-2 cursor-pointer"
                onClick={(e) => { e.stopPropagation(); if (post.authorId) navigate(`/profile/${post.authorId}`) }}
              >
                <div className="flex items-center gap-1">
                  <span className="font-bold text-[15px] text-gray-900 hover:underline">{post.author?.name || 'Anonymous'}</span>
                  {post.author?.verified && (
                    <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#1DA1F2"/><path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  )}
                </div>
                <div className="text-[13px] text-gray-500">@{post.author?.username || 'anonymous'}</div>
              </div>
              <p className="text-[14px] text-gray-800 leading-snug mb-3">
                On Record citizen journalist
              </p>
              <div className="flex items-center gap-4 text-[13px]">
                <span><strong className="text-gray-900">—</strong> <span className="text-gray-500">Following</span></span>
                <span><strong className="text-gray-900">—</strong> <span className="text-gray-500">Followers</span></span>
              </div>
            </div>
          )}
        </div>

        {/* Content — right side */}
        <div className="flex-1 min-w-0">
          {/* Header with More Button */}
          <div className="flex items-start justify-between">
            <div
              onMouseEnter={handleProfileEnter}
              onMouseLeave={handleProfileLeave}
            >
              <PostHeader
                author={post.author}
                onRecordCode={post.onRecordCode}
                timestamp={timeAgo}
                showAvatar={false}
                onUserClick={() => {
                  if (post.authorId) {
                    navigate(`/profile/${post.authorId}`)
                  }
                }}
              />
            </div>
            <button
              ref={moreButtonRef}
              onClick={handleMoreClick}
              className="rounded-full p-1 flex-shrink-0 relative transition-colors"
              style={{ color: 'var(--silver)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--solar)'
                e.currentTarget.style.background = 'var(--solar-glow)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--silver)'
                e.currentTarget.style.background = 'transparent'
              }}
            >
              <MoreHorizontal className="w-5 h-5" />
            </button>
          </div>

          {/* Post Text */}
          <div className="mt-1 whitespace-pre-wrap text-[15px] leading-5 text-gray-900">
            {post.content}
          </div>

          {/* Location */}
          {post.location?.address && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowMapModal(true)
              }}
              className="flex items-center gap-1 text-sm mt-1.5 mb-0.5 hover:underline transition-all text-gray-400"
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--solar)'}
              onMouseLeave={(e) => e.currentTarget.style.color = ''}
            >
              <MapPin className="w-3.5 h-3.5" />
              <span>{post.location.address}</span>
            </button>
          )}

          {/* Media — Twitter-style: container sized by media's natural aspect ratio */}
          {post.mediaUrl && (
            <div
              className="mt-3 inline-block rounded-2xl overflow-hidden cursor-pointer border border-gray-200"
              style={{ maxHeight: '510px', maxWidth: '100%' }}
              onClick={handleMediaClick}
            >
              {post.mediaType === 'video' ? (
                <VideoPlayer
                  src={post.mediaUrl}
                  autoPlay
                  controls
                  className="block"
                  style={{ maxHeight: '510px', maxWidth: '100%' }}
                />
              ) : (
                <img
                  src={post.mediaUrl}
                  alt="Post media"
                  className="block"
                  style={{ maxHeight: '510px', maxWidth: '100%' }}
                />
              )}
            </div>
          )}

          {/* Actions */}
          <PostActions
            stats={{
              likes: likesCount || post.stats?.likes || 0,
              comments: commentsCount || post.stats?.comments || 0,
              reposts: repostsCount || post.stats?.reposts || 0,
              views: post.stats?.views || 0,
            }}
            onLike={handleLike}
            onComment={handleComment}
            onRepost={handleRepost}
            onQuote={() => setShowQuoteModal(true)}
            onShare={handleShare}
            onBookmark={handleBookmark}
            onAnalytics={isOwner ? () => setShowAnalyticsModal(true) : undefined}
            isLiked={isLiked}
            isBookmarked={isBookmarked}
            isReposted={isReposted}
            loading={loading}
            isGuest={isGuest}
            hasMedia={!!post.mediaUrl}
            onRecordCode={post.onRecordCode}
            location={post.location}
            timeAgo={timeAgo}
            capturedAt={post.capturedAt}
            createdAt={post.createdAt}
          />
        </div>
      </div>

      {/* Options Dropdown Menu */}
      <PostOptionsDropdown
        isOpen={showOptionsMenu}
        onClose={() => setShowOptionsMenu(false)}
        isOwner={isOwner}
        post={{
          id: post.id,
          authorId: post.authorId,
          content: post.content,
          author: post.author,
        }}
        buttonPosition={menuPosition}
        showDelete={false} // Don't show delete in home feed
      />

      {/* Map Modal */}
      {post.location && (
        <MapModal
          isOpen={showMapModal}
          onClose={() => setShowMapModal(false)}
          location={post.location}
          postInfo={{
            onRecordCode: post.onRecordCode,
            timestamp: post.createdAt,
            username: post.author?.username,
          }}
        />
      )}

      {/* Location Permission Modal */}
      <LocationPermissionModal
        isOpen={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        onEnableLocation={handleEnableLocation}
      />

      {/* Quote Repost Modal */}
      <QuoteRepostModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
        currentUser={(() => { try { const u = JSON.parse(localStorage.getItem('user_profile') || '{}'); return { name: u.displayName || u.name, avatar: u.avatarUrl || u.avatar } } catch { return undefined } })()}
        post={{
          id: post.id,
          author: post.author,
          content: post.content,
          mediaUrl: post.mediaUrl,
          mediaType: post.mediaType,
          onRecordCode: post.onRecordCode,
        }}
      />

      <PostAnalyticsModal
        postId={post.id}
        isOpen={showAnalyticsModal}
        onClose={() => setShowAnalyticsModal(false)}
      />

    </article>
  )
}
